import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Gift, Sunrise, Flame, BookOpen, HandHeart, Users, Compass, ArrowRight, HeartHandshake, Sparkles, Megaphone } from 'lucide-react';
import { BRAND, HERO_IMAGES, PILLARS, FOUNDERS, VISION_MISSION, MANDATES } from '../mock';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import SectionHeading from '../components/SectionHeading';

const ICONS = { Heart, Gift, Sunrise, Flame, Compass, HeartHandshake, Sparkles, Megaphone };

export default function About() {
  return (
    <div>
      {/* Page Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.congregation} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/60" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">About the Ministry</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">
            Called to <span className="italic">dispense grace</span><br />in the name of Jesus.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-3xl">
            Dispensers of Grace Ministry is a Christ-centred, interdenominational Christian ministry &mdash; a family devoted to the Word of God and to the shepherding of souls into the fullness of Christ.
          </p>
        </div>
      </section>

      {/* Vision + Mission from ministry copy */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Vision & Mission"
            title={<>The Divine <span className="italic text-teal-deep">Prescription</span></>}
          />
          <p className="mt-8 text-lg leading-relaxed text-ink/75 text-center max-w-3xl mx-auto">{VISION_MISSION.intro}</p>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Card className="p-10 bg-white rounded-2xl border border-ink/5 shadow-sm">
              <BookOpen className="h-9 w-9 text-teal-deep mb-5" strokeWidth={1.4} />
              <h2 className="font-serif-display text-3xl text-ink mb-4">Our Vision</h2>
              <p className="font-serif-display italic text-xl text-teal-deep leading-snug">{VISION_MISSION.vision}</p>
            </Card>
            <Card className="p-10 bg-teal-deep text-white rounded-2xl shadow-lg">
              <Compass className="h-9 w-9 text-gold-brand mb-5" strokeWidth={1.4} />
              <h2 className="font-serif-display text-3xl mb-4">Our Mission</h2>
              <p className="text-white/85 leading-relaxed text-lg">{VISION_MISSION.mission}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Core message */}
      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-deep mb-4">Our Core Message</p>
          <p className="font-script text-5xl md:text-7xl text-teal-deep leading-none">God prescribes His Word,</p>
          <p className="font-script text-5xl md:text-7xl text-gold-brand leading-none mt-2">we dispense it.</p>
          <p className="mt-8 text-lg text-ink/75 leading-relaxed">
            The Great Physician has already written the prescription for every soul &mdash; the eternal, life-giving Word of God. It is our sacred, joyful assignment simply to dispense it. Faithfully. Warmly. To all who will receive.
          </p>
        </div>
      </section>

      {/* Our Mandates */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Our Mandates"
            title={<>Five Focus Areas of <span className="italic text-teal-deep">Grace</span></>}
            subtitle="The specific assignments the Great Physician has entrusted to Dispensers of Grace Ministry."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {MANDATES.map((m) => {
              const Icon = ICONS[m.icon] || Heart;
              return (
                <Card key={m.title} className="hover-lift group p-6 bg-white border border-ink/5 rounded-2xl text-center">
                  <div className="h-14 w-14 mx-auto rounded-xl bg-cream flex items-center justify-center mb-4 group-hover:bg-gold-brand transition-colors">
                    <Icon className="h-6 w-6 text-teal-deep group-hover:text-ink transition-colors" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif-display text-xl text-ink mb-2">{m.title}</h3>
                  <p className="text-sm text-ink/65 leading-relaxed">{m.text}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Four pillars */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="What We Stand For"
            title={<>The Four Pillars of Our <span className="italic text-teal-deep">Ministry</span></>}
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p) => {
              const Icon = ICONS[p.icon];
              return (
                <Card key={p.title} className="hover-lift group p-8 bg-white border border-ink/5 rounded-2xl">
                  <div className="h-14 w-14 rounded-xl bg-cream flex items-center justify-center mb-5 group-hover:bg-gold-brand transition-colors">
                    <Icon className="h-6 w-6 text-teal-deep" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif-display text-2xl text-ink">{p.title}</h3>
                  <p className="text-xs tracking-[0.25em] uppercase text-gold-deep font-medium mt-1 mb-3">{p.verse}</p>
                  <p className="text-sm text-ink/70 leading-relaxed">{p.text}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Meet the Founders"
            title={<>Our <span className="italic text-teal-deep">Shepherds</span></>}
            subtitle="Called and equipped to serve the Body of Christ together — in the Word, in prayer, and in the shepherding of souls."
          />
          <div className="mt-14 grid md:grid-cols-2 gap-8 lg:gap-12">
            {FOUNDERS.map((f) => (
              <Card key={f.name} className="p-6 md:p-8 bg-white rounded-2xl border border-ink/5 shadow-[0_10px_40px_-24px_rgba(15,34,49,0.25)]">
                <div className="flex flex-col sm:flex-row gap-6">
                  <div className="relative w-32 h-40 sm:w-40 sm:h-52 rounded-xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-teal-brand/20 to-gold-brand/20">
                    {f.photo ? (
                      <img src={f.photo} alt={f.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-teal-deep">
                        <span className="font-serif-display text-5xl">{f.name.split(' ').filter(w => w.length > 3).slice(-2).map(w => w[0]).join('')}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs tracking-[0.25em] uppercase text-gold-deep mb-2">{f.role}</p>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">{f.name}</h3>
                    <p className="mt-4 text-ink/70 leading-relaxed">{f.bio}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <div className="mt-10 flex justify-center gap-3 flex-wrap">
            <Link to="/teachings"><Button className="bg-teal-brand hover:bg-teal-deep text-white h-11 px-5">Watch Teachings</Button></Link>
            <Link to="/contact"><Button variant="outline" className="border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white h-11 px-5">Contact Us</Button></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-deep text-white text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <Users className="h-9 w-9 text-gold-brand mx-auto mb-5" strokeWidth={1.4} />
          <h2 className="font-serif-display text-3xl md:text-5xl">You&rsquo;re invited to belong.</h2>
          <p className="mt-4 text-white/80">Come as you are. Bring your questions, your burdens, your hopes. Jesus is enough for all of it.</p>
          <div className="mt-8 flex gap-3 justify-center flex-wrap">
            <Link to="/events"><Button className="bg-gold-brand hover:bg-[#C48A1F] text-ink h-11 px-5 font-semibold">Join a Service</Button></Link>
            <Link to="/prayer"><Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 h-11 px-5">Request Prayer</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
